import { ThingEventSnapshot } from '@eagle/core-data-types';
import { get } from 'lodash';
import { Contract, isArray, isEnum, isNumber, isObject, isRecord, isString, maybeArray, maybeRecord } from 'validata';
import { EventSummarizationFeatureDefinition, SummarizationMode, SummarizationRule } from './event-summarization-types';

export const matchEvent = (event: ThingEventSnapshot, match?: Record<string, any>): boolean => {
  if (!match || Object.keys(match).length === 0) return true;
  return Object.entries(match).every(([path, expected]) => {
    const actual = get(event, path) as unknown;
    return actual === expected;
  });
};

export const groupEvents = (events: ThingEventSnapshot[], keyCombination?: string[]): { grouped: Map<string, ThingEventSnapshot[]>; individual: ThingEventSnapshot[] } => {
  const groups = new Map<string, ThingEventSnapshot[]>();
  const individualEvents: ThingEventSnapshot[] = [];

  // Handle empty keyCombination - all events in one group
  if (!keyCombination || keyCombination.length === 0) {
    groups.set(SummarizationMode.All, events);
    return { grouped: groups, individual: individualEvents };
  }

  for (const event of events) {
    try {
      const keyParts = keyCombination.map((path) => get(event, path) as unknown);

      // Check for missing grouping keys - render individually
      if (keyParts.some((v) => v === undefined || v === null)) {
        individualEvents.push(event);
        continue;
      }

      const groupKey = keyParts.join('|');
      const group = groups.get(groupKey) ?? [];
      group.push(event);
      groups.set(groupKey, group);
    } catch (error) {
      individualEvents.push(event);
    }
  }

  return { grouped: groups, individual: individualEvents };
};

export const selectRepresentative = (events: ThingEventSnapshot[], mode: SummarizationMode): ThingEventSnapshot | ThingEventSnapshot[] => {
  if (mode === SummarizationMode.All) return events;

  const sorted = [...events].sort((a, b) =>
    new Date(a.occurred).getTime() - new Date(b.occurred).getTime(),
  );

  return mode === SummarizationMode.First ? sorted[0] : sorted[sorted.length - 1];
};

const matchRuleContract: Contract<SummarizationRule> = {
  minCount: isNumber(),
  match: maybeRecord(isString()),
  keyCombination: maybeArray(isString()),
};

const featureContract: Contract<EventSummarizationFeatureDefinition> = {
  eventTypeIds: isRecord(isEnum(SummarizationMode)),
  matchRules: isArray(isObject(matchRuleContract)),
};

export const eventSummarizationTemplateValidator = isRecord(isObject(featureContract));
